import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Button, IconButton, Stack } from '@mui/material';
import type { GridColDef, GridPaginationModel, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CasinoCategoryRow } from '../../@types/casino';
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import { useModal } from '../../contexts/ModalContext';
import { createEditColumn } from '../../helpers/columns';
import { buildCasinoCategoryUpdatePayload } from '../../helpers/casino';
import useMutateData from '../../hooks/useMutateData';
import { useCasinoCategories } from '../../queries/casino';
import { createColumn } from '../../utils';
import PositionInput from '../atoms/PositionInput';
import CasinoCategoryGamesModal from '../modals/CasinoCategoryGamesModal';
import TableTemplate from '../templates/TableTemplate';
import { createIncludeSelectionModel } from '../../utils/gridSelection';

const DEFAULT_PAGINATION_MODEL: GridPaginationModel = {
  page: 0,
  pageSize: 25,
};

const CasinoCategoriesPage = () => {
  const { openModal } = useModal();
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CasinoCategoryRow | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(DEFAULT_PAGINATION_MODEL);

  const { data: casinoCategories = [], isFetching, isLoading } = useCasinoCategories();
  const { updateData, updateDataAsync } = useMutateData(
    API_ROUTES.CASINO.CATEGORY_UPDATE,
    [QUERY_KEYS.casinoCategories],
    'casino'
  );

  const categoryRows = useMemo<CasinoCategoryRow[]>(
    () =>
      casinoCategories.map((category) => ({
        ...category,
        id: category._id,
      })),
    [casinoCategories]
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchValue?.trim().toLowerCase();

    if (!normalizedSearch) {
      return categoryRows;
    }

    return categoryRows.filter((category) => category.name.toLowerCase().includes(normalizedSearch));
  }, [categoryRows, searchValue]);

  useEffect(() => {
    if (!selectedCategory?.id) return;

    const updatedSelectedCategory = categoryRows.find((category) => category.id === selectedCategory.id) ?? null;
    setSelectedCategory(updatedSelectedCategory);
  }, [categoryRows, selectedCategory?.id]);

  const handleSearch = (value: string | null) => {
    setSearchValue(value);
    setPaginationModel((prevValue) => ({ ...prevValue, page: 0 }));
  };

  const handleManageGames = () => {
    if (!selectedCategory) return;

    openModal(<CasinoCategoryGamesModal category={selectedCategory} />, undefined, { dialogSize: 'wide' });
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleFavouriteToggle = useCallback(
    (category: CasinoCategoryRow) => {
      updateData(
        category.id,
        buildCasinoCategoryUpdatePayload({
          favourite: !category.favourite,
        }),
        undefined,
        'Category updated successfully.'
      );
    },
    [updateData]
  );

  const handlePositionChange = useCallback(
    (category: CasinoCategoryRow, position: number | null) => {
      return updateDataAsync(
        category.id,
        buildCasinoCategoryUpdatePayload({ position }),
        undefined,
        'Category updated successfully.'
      );
    },
    [updateDataAsync]
  );

  const handleRowClick = (params: GridRowParams<CasinoCategoryRow>) => {
    setSelectedCategory(params.row);
  };

  const columns = useMemo<GridColDef<CasinoCategoryRow>[]>(
    () => [
      createColumn('_id', 'ID', {
        minWidth: 220,
        sortable: false,
      }),
      createColumn('name', 'Name', {
        minWidth: 220,
        flex: 1,
      }),
      createColumn('position', 'Position', {
        minWidth: 120,
        renderCell: ({ row }: GridRenderCellParams<CasinoCategoryRow>) => (
          <PositionInput value={row.position} onCommit={(position) => handlePositionChange(row, position)} />
        ),
      }),
      createColumn('favourite', 'Favourite', {
        minWidth: 120,
        renderCell: ({ row }: GridRenderCellParams<CasinoCategoryRow>) => (
          <IconButton
            color='primary'
            onClick={(event) => {
              event.stopPropagation();
              handleFavouriteToggle(row);
            }}
          >
            {row.favourite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>
        ),
      }),
      createEditColumn<CasinoCategoryRow>({
        columnName: 'Edit',
        icon: <EditOutlinedIcon />,
      }),
    ],
    [handleFavouriteToggle, handlePositionChange]
  );

  return (
    <TableTemplate
      rows={filteredRows}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={filteredRows.length}
      handleSearch={handleSearch}
      handlePaginationModelChange={handlePaginationModelChange}
      paginationModel={paginationModel}
      paginationMode='client'
      sortingMode='client'
      onRowClick={handleRowClick}
      rowSelectionModel={createIncludeSelectionModel(selectedCategory ? [selectedCategory.id] : [])}
      createItemBtnLabel='New Category'
      formType='categories'
      showFilter={false}
      customTableHeaderContent={
        <Stack
          direction='row'
          sx={{
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Button variant='contained' onClick={handleManageGames} disabled={!selectedCategory}>
            Manage Games
          </Button>
        </Stack>
      }
      slotProps={{
        row: {
          style: {
            cursor: 'pointer',
          },
        },
      }}
    />
  );
};

export default CasinoCategoriesPage;
